import { useState, useMemo } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EVENT, formatPitchDate, buildICS, downloadICS, getPhase } from "@/lib/phase";
import { proxiedImage } from "@/lib/api";
import LivestreamGateModal, { isUnlocked, getUnlockedUrl } from "@/components/LivestreamGateModal";

function toEmbedURL(url) {
  if (!url) return "";
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  // Fathom and other hosts that block iframes — handled separately in the UI
  if (/fathom\.video|loom\.com\/share|drive\.google\.com\/file/i.test(url)) return "";
  return url;
}

export { toEmbedURL };

const STATUS_STYLES = {
  "Upcoming": "bg-emerald-100 text-emerald-800 border-emerald-300",
  "Live Now": "bg-rose-500 text-white border-rose-600 animate-pulse",
  "Pitch Complete": "bg-slate-100 text-slate-700 border-slate-300",
  "Replay Available": "bg-amber-300 text-emerald-950 border-amber-400",
  "Voting Closed": "bg-slate-200 text-slate-600 border-slate-400",
  "Cancelled": "bg-slate-200 text-slate-500 border-slate-300 line-through",
};

const SCHEDULE_STATUS_STYLES = {
  "Confirmed": "bg-emerald-700 text-white border-emerald-800",
  "Tentative": "bg-amber-200 text-emerald-950 border-amber-400",
  "Awaiting Information": "bg-slate-200 text-slate-700 border-slate-400",
  "Completed": "bg-slate-100 text-slate-600 border-slate-300",
};

function StatusBadge({ status }) {
  const cls = STATUS_STYLES[status] || STATUS_STYLES["Upcoming"];
  return (
    <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full border font-[Outfit] font-bold ${cls}`}>
      {status === "Live Now" && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
      {status}
    </span>
  );
}

function ScheduleBadge({ status }) {
  if (!status || status === "Confirmed") return null;
  const cls = SCHEDULE_STATUS_STYLES[status] || SCHEDULE_STATUS_STYLES["Confirmed"];
  return (
    <span className={`inline-flex text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full border font-[Outfit] font-bold ${cls}`}>
      {status}
    </span>
  );
}

function BusinessCard({ b, externalVotingUrl, onShare, phase, votingClosed, onRequestLivestream }) {
  const voteUrl = b.vote_url || externalVotingUrl;
  const replay = b.replay_link || b.video_url;
  const embed = toEmbedURL(replay);
  const showVote = !votingClosed && phase <= 3;
  const businessName = b.business_name || "";

  return (
    <Card data-testid={`business-card-${b.id}`} className="overflow-hidden border border-emerald-200 bg-white hover:border-emerald-400 transition-colors flex flex-col">
      {embed ? (
        <div className="aspect-video bg-emerald-950">
          <iframe src={embed} title={b.name} className="w-full h-full" allowFullScreen allow="accelerometer; encrypted-media; picture-in-picture" />
        </div>
      ) : (
        <div className="aspect-[4/5] bg-emerald-100 relative overflow-hidden flex items-center justify-center">
          <div className="font-[Cormorant_Garamond] text-emerald-800 text-6xl select-none" aria-hidden="true">
            {b.name?.slice(0, 1)}
          </div>
          {b.photo_url && (
            <img
              src={proxiedImage(b.photo_url)}
              alt={b.name}
              loading="lazy"
              decoding="async"
              referrerPolicy="no-referrer"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ backgroundColor: "transparent" }}
            />
          )}
        </div>
      )}
      <div className="p-5 space-y-3 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            {b.interview_time && (
              <div className="text-[10px] uppercase tracking-[0.25em] text-rose-600 font-[Outfit] font-bold">
                {formatPitchDate(b.pitch_day)} · {b.interview_time}
              </div>
            )}
            <h3 className="font-[Cormorant_Garamond] text-2xl text-emerald-950 mt-1 leading-tight">{b.name}</h3>
            {businessName && (
              <div className="text-sm text-emerald-800 font-[Outfit] font-semibold mt-0.5">{businessName}</div>
            )}
          </div>
          <div className="flex flex-col gap-1.5 items-end shrink-0">
            <ScheduleBadge status={b.schedule_status} />
            {b.status && <StatusBadge status={b.status} />}
          </div>
        </div>
        {b.bio && <p className="text-sm text-slate-700 font-[Outfit] leading-relaxed line-clamp-3">{b.bio}</p>}
        {!b.bio && b.description && <p className="text-sm text-slate-700 font-[Outfit] leading-relaxed line-clamp-3">{b.description}</p>}
        <div className="flex flex-wrap gap-2 pt-1 mt-auto">
          {b.status === "Live Now" && b.livestream_url && (
            <Button
              size="sm"
              data-testid={`watch-${b.id}`}
              onClick={() => onRequestLivestream?.(b.pitch_day, b.livestream_url)}
              className="rounded-full bg-rose-500 hover:bg-rose-600 text-white font-[Outfit] font-semibold"
            >
              ▶ Watch Live
            </Button>
          )}
          {b.livestream_url && b.status === "Upcoming" && phase <= 2 && (
            <Button
              size="sm"
              variant="outline"
              data-testid={`join-zoom-${b.id}`}
              onClick={() => onRequestLivestream?.(b.pitch_day, b.livestream_url)}
              className="rounded-full border-emerald-400 text-emerald-800 hover:bg-emerald-50 font-[Outfit]"
            >
              Join Zoom
            </Button>
          )}
          {b.status === "Replay Available" && replay && !embed && (
            <Button asChild size="sm" data-testid={`replay-${b.id}`} className="rounded-full bg-emerald-800 hover:bg-emerald-900 text-white font-[Outfit] font-semibold">
              <a href={replay} target="_blank" rel="noreferrer">Watch Replay</a>
            </Button>
          )}
          {showVote ? (
            <Button asChild size="sm" data-testid={`vote-btn-${b.id}`} className="rounded-full bg-rose-500 hover:bg-rose-600 text-white font-[Outfit] font-semibold">
              <a href={voteUrl} target="_blank" rel="noreferrer">Vote for This Business</a>
            </Button>
          ) : phase === 4 ? (
            <span className="rounded-full bg-slate-100 text-slate-600 text-xs px-3 py-1 font-[Outfit]">Voting Has Closed</span>
          ) : null}
          <Button size="sm" variant="outline" data-testid={`share-btn-${b.id}`} onClick={() => onShare(b)} className="rounded-full border-emerald-400 text-emerald-800 hover:bg-emerald-50 font-[Outfit]">
            Share
          </Button>
        </div>
      </div>
    </Card>
  );
}

export default function PitchSchedule({ settings, days, businesses, externalVotingUrl, sectionId }) {
  const phase = getPhase(settings);
  const todayISO = new Date().toISOString().slice(0, 10);
  const defaultTab = EVENT.pitchDays.includes(todayISO) ? todayISO : EVENT.pitchDays[0];
  const [tab, setTab] = useState(defaultTab);
  const votingClosed = phase >= 4;
  const [gateOpen, setGateOpen] = useState(false);
  const [gatePitchDay, setGatePitchDay] = useState(null);

  const handleRequestLivestream = (pitchDay, fallbackUrl) => {
    // If already unlocked for that night, go straight to the link
    if (isUnlocked(pitchDay)) {
      const url = getUnlockedUrl(pitchDay) || fallbackUrl;
      if (url) window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    setGatePitchDay(pitchDay);
    setGateOpen(true);
  };

  const byDay = useMemo(() => {
    const m = {};
    EVENT.pitchDays.forEach((d) => (m[d] = []));
    (businesses || []).forEach((b) => {
      if (m[b.pitch_day]) m[b.pitch_day].push(b);
    });
    return m;
  }, [businesses]);

  const dayMap = useMemo(() => {
    const m = {};
    (days || []).forEach((d) => (m[d.date] = d));
    return m;
  }, [days]);

  // Live now + Up next groupings (phase 2 only)
  const liveNow = (businesses || []).filter((b) => b.status === "Live Now");
  const upNext = (businesses || [])
    .filter((b) => b.status === "Upcoming" && b.pitch_day === todayISO)
    .slice(0, 3);

  const handleAddToCal = (date, startTime) => {
    const start = new Date(`${date}T19:00:00-04:00`);
    const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
    downloadICS(`business-showers-${date}.ics`, buildICS({
      title: `Business Showers Pitches — ${formatPitchDate(date)}`,
      description: `Live pitches with our participating entrepreneurs. ${startTime || ""}`,
      startISO: start.toISOString(),
      endISO: end.toISOString(),
      location: "Online (Zoom)",
      url: "https://www.businessshowers.org/contest",
    }));
  };

  const handleShare = (b) => {
    const url = "https://www.businessshowers.org/contest";
    const text = `Watch ${b.name} pitch at Business Showers — and vote!`;
    if (navigator.share) {
      navigator.share({ title: b.name, text, url }).catch(() => {});
    } else {
      navigator.clipboard.writeText(`${text} ${url}`);
      alert("Share link copied to clipboard.");
    }
  };

  const sectionTitle = phase === 4
    ? "Meet the 2026 Honorees"
    : phase === 5
    ? "2026 Honoree Interview & Pitches · Archive"
    : "Honoree Interview & Pitches";

  return (
    <section id={sectionId} data-testid="pitch-schedule" className="bg-white py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="max-w-3xl mb-10">
          <div className="text-[11px] uppercase tracking-[0.3em] text-rose-600 font-[Outfit] font-bold">
            Finalist Live Pitches · June 16, 17 &amp; 18, 2026 · Live on Zoom
          </div>
          <h2 className="font-[Cormorant_Garamond] text-4xl sm:text-5xl text-emerald-950 mt-3 leading-tight">
            {sectionTitle}
          </h2>
          <p className="text-slate-700 font-[Outfit] mt-4 leading-relaxed">
            Meet this year&apos;s honorees across three evenings of live interviews and pitches.
            {phase <= 3 && " After each interview, your $2 community vote moves a business forward."}
          </p>
        </div>

        {/* Live Now / Up Next (phase 2 only) */}
        {phase === 2 && (liveNow.length || upNext.length) && (
          <div className="mb-10 grid grid-cols-1 md:grid-cols-2 gap-4">
            {liveNow.length > 0 && (
              <div className="bg-rose-500 text-white p-6 rounded-lg">
                <div className="text-[10px] uppercase tracking-[0.3em] font-[Outfit] font-bold flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-white animate-pulse" />
                  Live Now
                </div>
                <ul className="mt-3 space-y-1">
                  {liveNow.map((b) => (
                    <li key={b.id} className="font-[Cormorant_Garamond] text-2xl">{b.name}</li>
                  ))}
                </ul>
              </div>
            )}
            {upNext.length > 0 && (
              <div className="bg-amber-300 text-emerald-950 p-6 rounded-lg">
                <div className="text-[10px] uppercase tracking-[0.3em] font-[Outfit] font-bold">Up Next Today</div>
                <ul className="mt-3 space-y-1">
                  {upNext.map((b) => (
                    <li key={b.id} className="font-[Cormorant_Garamond] text-xl">{b.name}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList data-testid="pitch-day-tabs" className="bg-emerald-50 border border-emerald-200 rounded-full p-1 h-auto">
            {EVENT.pitchDays.map((d) => (
              <TabsTrigger
                key={d}
                value={d}
                data-testid={`pitch-tab-${d}`}
                className="data-[state=active]:bg-emerald-800 data-[state=active]:text-white rounded-full px-5 py-2 font-[Outfit] text-sm font-semibold"
              >
                {formatPitchDate(d)}
              </TabsTrigger>
            ))}
          </TabsList>

          {EVENT.pitchDays.map((d) => {
            const meta = dayMap[d] || {};
            const list = byDay[d] || [];
            return (
              <TabsContent key={d} value={d} className="mt-8 space-y-8">
                {(() => {
                  const allComplete = list.length > 0 && list.every((b) => b.status === "Pitch Complete");
                  return (
                <div className="bg-white border border-emerald-200 rounded-lg p-6 sm:p-8 flex flex-col md:flex-row gap-6 md:items-center md:justify-between">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.25em] text-rose-600 font-[Outfit] font-bold">
                      {allComplete ? `Pitches Complete · ${formatPitchDate(d)}` : `Finalist Live Pitches · ${formatPitchDate(d)} · Starting ${meta.start_time || "5:30 PM ET"}`}
                    </div>
                    <div className="font-[Cormorant_Garamond] text-2xl sm:text-3xl text-emerald-950 mt-1">
                      {allComplete
                        ? "Thanks for tuning in — replays coming soon"
                        : `${list.length} ${list.length === 1 ? "honoree" : "honorees"} this evening`}
                    </div>
                    {meta.note && <p className="text-sm text-slate-600 font-[Outfit] mt-2">{meta.note}</p>}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {allComplete ? null : meta.zoom_url ? (
                      <Button
                        data-testid={`watch-live-${d}`}
                        onClick={() => handleRequestLivestream(d, meta.zoom_url)}
                        className="rounded-full bg-rose-500 hover:bg-rose-600 text-white font-[Outfit] font-semibold"
                      >
                        ▶ Watch Live
                      </Button>
                    ) : (
                      <Button data-testid={`watch-live-${d}`} disabled className="rounded-full bg-slate-200 text-slate-500 font-[Outfit]">
                        Zoom link coming soon
                      </Button>
                    )}
                    {!allComplete && (
                      <Button data-testid={`add-cal-${d}`} variant="outline" onClick={() => handleAddToCal(d, meta.start_time)} className="rounded-full border-emerald-400 text-emerald-800 hover:bg-emerald-50 font-[Outfit]">
                        Add to Calendar
                      </Button>
                    )}
                    {!votingClosed && (
                      <Button asChild className="rounded-full bg-amber-300 hover:bg-amber-400 text-emerald-950 font-[Outfit] font-semibold">
                        <a href={externalVotingUrl} target="_blank" rel="noreferrer">Vote Now</a>
                      </Button>
                    )}
                  </div>
                </div>
                  );
                })()}

                {list.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {list.map((b) => (
                      <BusinessCard
                        key={b.id}
                        b={b}
                        externalVotingUrl={externalVotingUrl}
                        onShare={handleShare}
                        phase={phase}
                        votingClosed={votingClosed}
                        onRequestLivestream={handleRequestLivestream}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="border border-dashed border-emerald-300 bg-emerald-50 p-10 text-center text-slate-600 font-[Outfit] rounded-lg">
                    Featured businesses for {formatPitchDate(d)} will be announced soon.
                  </div>
                )}

                {phase <= 3 && (
                  <div className="bg-emerald-900 text-white p-6 sm:p-8 rounded-lg flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.25em] text-amber-300 font-[Outfit] font-bold">After the Pitch</div>
                      <div className="font-[Cormorant_Garamond] text-2xl mt-1">
                        You heard their stories. Now help move a business forward.
                      </div>
                      <p className="text-sm text-emerald-50 font-[Outfit] mt-2">
                        Community voting closes June 22, and every $2 vote makes a difference.
                      </p>
                    </div>
                    <Button asChild className="rounded-full bg-amber-300 hover:bg-amber-400 text-emerald-950 px-6 h-11 font-[Outfit] font-semibold">
                      <a href={externalVotingUrl} target="_blank" rel="noreferrer">Cast Your $2 Vote</a>
                    </Button>
                  </div>
                )}
              </TabsContent>
            );
          })}
        </Tabs>
      </div>
      <LivestreamGateModal
        open={gateOpen}
        onOpenChange={setGateOpen}
        pitchDay={gatePitchDay}
      />
    </section>
  );
}
